<template>
    <div class="recipient-list">
        <div v-click-outside="handleBlur" class="recipient-list-content" @click.stop="focusSearch">
            <div :class="['recipients', { focus: hasFocus, invalid }]">
                <div ref="recipientChips" class="recipient-chips">
                    <ds-chip
                        v-for="{ id, name, icon } in recipientCriteria"
                        :key="id"
                        class="recipient-chip"
                        remove
                        with-interaction
                        @click.native="handleRemove(id)"
                    >
                        <span>{{ name }}</span>

                        <ds-icon slot="leading" :name="icon" />
                    </ds-chip>
                </div>

                <input
                    ref="searchBox"
                    v-model="query"
                    type="text"
                    class="search-box"
                    name="search"
                    autocomplete="no"
                    @blur="handleBlur"
                    @focus="handleFocus"
                    @input="search"
                    @keydown.up.prevent="dropdownKeys_arrowUp"
                    @keydown.down.prevent="dropdownKeys_arrowDown"
                    @keydown.enter.prevent.stop.self="handleEnterPress"
                    @keydown.tab.stop="closeSearch"
                    @keydown.esc.stop="closeSearch"
                    @keydown.delete.stop="removeLast"
                />

                <label :class="{ active: isActive }" for="search">{{ 'To' }}</label>
            </div>

            <ds-dropdown :is-open="isSearchDropdownOpen" class="recipient-options" @close="closeSearch">
                <template #menu>
                    <ds-multiselect-menu
                        ref="dropdownMenu"
                        label-prop="name"
                        value-prop="id"
                        help-text
                        :filter="query"
                        :options="filteredResults"
                        :pointer.sync="dropdownKeys_pointer"
                        @input="handleSearchSelect"
                        @updateVisibleOptions="updateVisibleOptions"
                    />
                </template>
            </ds-dropdown>

            <div class="search-description">
                <span class="to-description" :class="{ invalid }">
                    {{ toDescription }}
                </span>

                <span :class="['contact-count-message', { 'no-recipients': noRecipients }]"> {{ contactCountMessage }} </span>
            </div>
        </div>

        <sms-broadcast-recipient-tooltip />
    </div>
</template>

<script>
import debounce from 'lodash.debounce';
import dropdownKeys from '@/mixins/dropdownKeys';
import { searchRecipients } from '@/api/broadcasts/smsBroadcasts';
import { INPUT_DEBOUNCE_DELAY } from '@/components/shared/constants/timing.constants';
import SmsBroadcastRecipientTooltip from '@/components/broadcast/sms/SmsBroadcastRecipientTooltip';

export default {
    components: {
        SmsBroadcastRecipientTooltip,
    },

    mixins: [dropdownKeys],

    props: {
        contactCount: {
            type: Number,
            default: 0,
        },
        inputDelay: {
            type: Number,
            default: INPUT_DEBOUNCE_DELAY,
        },
        invalid: Boolean,
        optStatuses: {
            type: Array,
            default: () => [],
        },
        recipientCriteria: {
            type: Array,
            default: () => [],
        },
        recipients: {
            type: Array,
            default: () => [],
        },
    },

    data() {
        return {
            hasFocus: false,
            isSearchDropdownOpen: false,
            query: '',
            searchResults: [],
        };
    },

    mounted() {
        if (this.inputDelay) {
            this.search = debounce(this.search, this.inputDelay);
        }
    },

    computed: {
        contactCountMessage() {
            const contactCount = this.contactCount.toString();

            return `${contactCount} ${this.contactText(this.contactCount)}`;
        },

        noRecipients() {
            return this.invalid && this.contactCount === 0;
        },

        filteredResults() {
            return this.searchResults.filter((result) => {
                return !this.recipientCriteria.some((selected) => selected.id === result.id);
            });
        },

        isActive() {
            return Boolean(this.query) || this.recipientCriteria.length > 0;
        },

        toDescription() {
            if (this.invalid && this.recipientCriteria.length === 0) {
                return 'Add at least 1 contact';
            }

            if (this.recipientCriteria.length === 0) {
                return 'Type saved searches, tags, or contact names';
            }

            return '';
        },
    },

    methods: {
        contactText(contactCount) {
            if (contactCount === 0 || contactCount > 1) {
                return 'recipients';
            }

            return 'recipient';
        },

        closeSearch() {
            this.isSearchDropdownOpen = false;
        },

        focusSearch() {
            this.$refs.searchBox.focus();
        },

        handleBlur() {
            this.hasFocus = false;
            this.$emit('blur');
        },

        handleEnterPress() {
            this.closeSearch();
            this.dropdownKeys_enter();
        },

        handleFocus() {
            this.hasFocus = true;
            this.$emit('focus');
        },

        handleRemove(id) {
            const recipientCriteria = this.recipientCriteria.filter((recipient) => recipient.id !== id);

            this.$emit('update', {
                recipientCriteria,
            });
        },

        handleSearchSelect(selection) {
            this.closeSearch();
            this.query = '';
            const recipientCriteria = [...this.recipientCriteria, selection];

            this.$emit('update', { recipientCriteria });
            this.scrollToBottom();
        },

        // method used by dropdownKeys mixin
        onEnter(option) {
            this.$refs.dropdownMenu.onEnter(option);
        },

        removeLast() {
            if (this.query.length === 0 && this.recipientCriteria.length > 0) {
                const { id } = this.recipientCriteria[this.recipientCriteria.length - 1];

                this.handleRemove(id);
            }
            this.scrollToBottom();
        },

        async scrollToBottom() {
            await this.$nextTick();

            const { recipientChips } = this.$refs;

            recipientChips.scrollTop = recipientChips.scrollHeight;
        },

        async search(event) {
            this.query = event.target.value;

            const searchError = 'There was an error searching';

            if (this.query.length > 0) {
                try {
                    const params = {
                        query: this.query,
                    };
                    const { smsRecipientQueryResponseItems: recipients } = await searchRecipients(params);

                    if (Array.isArray(recipients) && recipients.length > 0) {
                        this.searchResults = recipients;

                        this.isSearchDropdownOpen = true;
                    }
                } catch (error) {
                    this.$toast({ message: searchError });
                }
            } else {
                this.closeSearch();
            }
        },

        updateVisibleOptions(options) {
            this.dropdownKeys_optionsModel = options;
        },
    },
};
</script>

<style lang="scss" scoped>
    .recipient-list {
        display: flex;
    }

    .recipient-list-content {
        display: flex;
        flex-direction: column;
        width: 100%;

        @media($small) {
            max-width: initial;
        }
    }

    .recipient-chip {
        margin: $spacing-100;
        --text-overflow: none;
    }

    .recipients {
        @include input-field;
        display: flex;
        width: 100%;
        border: 1px solid $color-gray-400;
        border-radius: $spacing-100;
        margin-bottom: 0;

        &.focus {
            @include transition(border);
            border-color: $color-blue;
        }

        &.invalid {
            @include transition(border);
            border-color: $color-red;

            label, input:focus ~ label {
                color: $color-red;
            }
        }

        input {
            @include input-text;
            border: none;
            --input-background: #{$color-paper};

            &:active, &:focus {
                border: none;
            }

            &:focus ~ label {
                @include pill-label;
            }
        }

        label {
            &.active {
                @include pill-label;
            }
        }
    }

    .recipient-chips {
        @include overflowY;
        max-height: px-to-rem(214px);
    }

    .to-description {
        font-size: $font-size-xs;
        color: $color-ink-800;

        &.invalid {
            color: $color-red
        }
    }

    .search-description {
        display: flex;
        align-items: flex-start;
        justify-content: space-between;
        margin-bottom: $spacing-200;
    }

    .popover-container {
        --popover-width: #{px-to-rem(250px)};

        @media($small) {
            --popover-width: #{px-to-rem(175px)};
        }
    }
    .recipients > label {
        top: 3px;
    }

    .contact-count-message {
        color: $color-text-subtle;
        font-size: $font-size-xs;

        &.no-recipients {
            color: $color-red;
        }
    }
</style>
